// useEditLease.js
// The advisory edit lease (services/editLease.js) for an editor that edits a
// document IN PLACE: it shows the live document read-only until the person
// presses Edit, holds the lease while they edit, and releases it when they
// save, cancel, move on, or the editor unmounts.
//
//   no session  ─► 'none'
//   session     ─► 'acquiring' ─► 'held' (heartbeat every LEASE_HEARTBEAT_MS)
//                              └─► 'blocked' (someone else holds it; takeOver())
//                              └─► 'unavailable' (the lease could not be read)
//   while held   ─► 'lost' (another session took it) | 'deleted' (document gone)
//
// A lease granted after the caller stopped wanting it (Cancel pressed during
// the acquire, the editor unmounted) is released at once rather than left to
// expire. `forget()` is for a save that released the lease in its own write.
//
// `sessionId` IS the editing session: the caller mints one when the session
// starts and passes null when it ends, which is what `wanted` used to say. One
// identity per session matters because a draft write is authorised by the
// session id alone (services/functionTriggers.js), so an editor that was taken
// over must never come back under the identity the lease still names.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LEASE_HEARTBEAT_MS,
  acquireEditLease,
  consumeSessionMount,
  heartbeatEditLease,
  registerSessionMount,
  releaseEditLease,
  takeOverEditLease,
} from '../services/editLease';

export default function useEditLease({ ref, uid, holderName, noun, sessionId }) {
  const heldRef = useRef(false);
  // The one claim of this editing session, so the release path can wait for a
  // claim still in flight, and a remount does not claim again.
  const claimRef = useRef(null);
  const mountTokenRef = useRef(null);
  // The session this hook is being asked for right now, so work that started in
  // a session which has since ended does not report back into the editor.
  const currentSessionRef = useRef(sessionId);
  currentSessionRef.current = sessionId;
  // The release of the session that just ended, while it is still in flight.
  // One session ends by handing the lease back and the next claims the same
  // document, and those must not race: a claim that overtakes the release it
  // follows is refused by the lease the outgoing session is still holding, and
  // the person is blocked by their own previous session.
  const releaseInFlightRef = useRef(null);
  const [state, setState] = useState('none');
  const [blockingLease, setBlockingLease] = useState(null);
  const [takeoverBusy, setTakeoverBusy] = useState(false);

  const release = useCallback(async (sessionKey, mountToken) => {
    if (!ref) return;
    // A grant still in flight has not set heldRef yet, so wait for it rather
    // than read the flag too early and leave the lease on the document until it
    // expires. A failed claim reports itself and leaves nothing to release.
    const claim = claimRef.current;
    if (claim) await claim.request.catch(() => {});
    // Another mount is editing under this session now, so the lease is being
    // used: this one is on its way out and has nothing to hand back.
    if (!consumeSessionMount(sessionKey, mountToken)) return;
    if (!heldRef.current) return;
    heldRef.current = false;
    await releaseEditLease(ref, sessionId);
  }, [ref, sessionId]);
  const releaseRef = useRef(release);
  useEffect(() => { releaseRef.current = release; }, [release]);

  useEffect(() => {
    if (!sessionId || !ref) {
      setState('none');
      setBlockingLease(null);
      return undefined;
    }
    // One editing session of one document: the claim and the mount registry are
    // both keyed by it, so a remount rejoins the session while a move to another
    // document still hands back the lease it leaves behind. This mount is the
    // one editing that key until another replaces it, and its release presents
    // this token to prove it.
    const sessionKey = `${sessionId}/${ref.path || ref}`;
    const mountToken = registerSessionMount(sessionKey);
    mountTokenRef.current = mountToken;
    // A remount is not a new session and does not claim again.
    if (claimRef.current?.claimKey !== sessionKey) {
      setState('acquiring');
      const previousRelease = releaseInFlightRef.current;
      const acquire = () => acquireEditLease(ref, { uid, holderName, sessionId }, noun);
      const claim = {
        claimKey: sessionKey,
        request: previousRelease ? previousRelease.then(acquire) : acquire(),
      };
      claimRef.current = claim;
      claim.request
        .then((result) => {
          // A grant puts the lease on the document whatever happens next, so
          // record it before anything else: the release path reads this flag to
          // hand the lease back instead of leaving it to expire.
          if (result.granted) heldRef.current = true;
          if (claimRef.current !== claim) return;
          if (result.granted) {
            setState('held');
          } else {
            setBlockingLease(result.lease);
            setState('blocked');
          }
        })
        .catch((err) => {
          if (claimRef.current !== claim) return;
          console.error(`Failed to acquire the ${noun} edit lease:`, err);
          setBlockingLease(null);
          setState('unavailable');
        });
    }
    return () => {
      // Kept so the next session's claim can follow this release rather than
      // race it. It never rejects, so it can simply be waited on.
      releaseInFlightRef.current = releaseRef.current(sessionKey, mountToken)
        .catch((err) => console.error(`Failed to release the ${noun} edit lease:`, err));
    };
  }, [sessionId, ref, uid, holderName, noun]);

  useEffect(() => {
    if (state !== 'held' || !ref) return undefined;
    const timer = setInterval(async () => {
      try {
        const outcome = await heartbeatEditLease(ref, sessionId);
        if (outcome !== 'held') {
          heldRef.current = false;
          setState(outcome);
        }
      } catch (err) {
        console.error(`${noun} edit lease heartbeat failed:`, err);
      }
    }, LEASE_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [state, ref, noun, sessionId]);

  const takeOver = useCallback(async () => {
    if (!ref || !sessionId) return;
    setTakeoverBusy(true);
    // A takeover acquires this session's lease just as the claim does, so it
    // becomes the claim the release waits for: an editor whose session ended
    // while this was in flight hands the lease straight back instead of leaving
    // a dead session's lease on the document for the full 15 minutes.
    const takeover = {
      claimKey: `${sessionId}/${ref.path || ref}`,
      request: takeOverEditLease(ref, { uid, holderName, sessionId }, noun),
    };
    claimRef.current = takeover;
    try {
      await takeover.request;
      // The lease is on the document from here on, whatever happened meanwhile.
      heldRef.current = true;
      if (currentSessionRef.current !== sessionId) return;
      setBlockingLease(null);
      setState('held');
    } catch (err) {
      console.error(`Failed to take over the ${noun} edit lease:`, err);
      if (currentSessionRef.current === sessionId) setState('unavailable');
    } finally {
      if (currentSessionRef.current === sessionId) setTakeoverBusy(false);
    }
  }, [ref, uid, holderName, noun, sessionId]);

  // The live document shows the lease is no longer ours.
  const markLost = useCallback(() => {
    heldRef.current = false;
    setState('lost');
  }, []);

  // A write of ours already released the lease.
  const forget = useCallback(() => {
    heldRef.current = false;
  }, []);

  return {
    state,
    sessionId,
    blockingLease,
    takeoverBusy,
    takeOver,
    markLost,
    forget,
  };
}
